<template>
  <div
    :key="module.key"
    class="kpi-outline">
    <h3 class="group-heading">
      Measures
    </h3>
    <vuedraggable
      group="kpiMeasures"
      ghost-class="ghost"
      class="dropzone"
      :list="module.kpi_measures">
      <div
        v-for="(kpiMeasure, i) in module.kpi_measures"
        :key="kpiMeasure.key || i">
        <b-tooltip
          style="width: 100%"
          position="is-right"
          type="is-light"
          :append-to-body="true"
          multilined>
          <template #content>
            <div class="data-tooltip">
              <div>
                <h2>Measure:</h2>
                {{
                  kpiMeasure?.measure_config?.measure?.name ||
                    kpiMeasure?.measure_config?.measure?.key ||
                    "Null"
                }}
              </div>
            </div>
          </template>
          <b-tooltip
            style="width: 100%"
            type="is-light"
            position="is-bottom"
            :triggers="['contextmenu']"
            size="is-small"
            multilined
            :auto-close="['outside', 'escape', 'inside']">
            <div
              class="row mini-view-item"
              :class="{
                selected: isSelected('measure', i),
                'error-border': childErrors(i, measureErrors).length > 0,
              }"
              @click="select('measure', i)"
              @contextmenu.prevent="">
              <img
                src="@/assets/icons/kpi-measure.svg"
                alt="" />
              <div class="item-name">
                {{ kpiMeasure.name || "KPI Measure" }}
              </div>
              <i
                v-if="isHero(i)"
                class="material-icons hero-marker"
                title="Rendered as this module's headline">
                star
              </i>
            </div>
            <template #content>
              <div class="context-menu-actions">
                <button
                  class="button is-light"
                  @click="duplicateMeasure(i)">
                  Duplicate
                </button>
                <button
                  class="button is-danger is-light"
                  @click="deleteMeasure(i)">
                  Delete
                </button>
              </div>
            </template>
          </b-tooltip>
        </b-tooltip>
      </div>
    </vuedraggable>

    <button
      class="mini-view-item button add-button"
      @click="addMeasure">
      <img
        src="@/assets/icons/add-row.svg"
        alt="" />
      <div>Add measure</div>
    </button>

    <h3 class="group-heading">
      Primary row
    </h3>
    <div
      class="row mini-view-item"
      :class="{
        selected: isSelected('primary', 0),
        'error-border': primaryRowErrors.length > 0,
      }"
      @click="select('primary', 0)">
      <img
        src="@/assets/icons/kpi-row.svg"
        alt="" />
      <div>{{ module.primary_row?.name || "Primary value" }}</div>
    </div>

    <h3 class="group-heading">
      Benchmarks
    </h3>
    <vuedraggable
      group="kpiBenchmarks"
      ghost-class="ghost"
      class="dropzone"
      :list="module.benchmarks">
      <div
        v-for="(benchmark, i) in module.benchmarks"
        :key="benchmark.key || i">
        <b-tooltip
          style="width: 100%"
          type="is-light"
          position="is-bottom"
          :triggers="['contextmenu']"
          size="is-small"
          multilined
          :auto-close="['outside', 'escape', 'inside']">
          <div
            class="row mini-view-item secondary"
            :class="{
              selected: isSelected('benchmark', i),
              'error-border': childErrors(i, benchmarkErrors).length > 0,
            }"
            @click="select('benchmark', i)"
            @contextmenu.prevent="">
            <img
              src="@/assets/icons/kpi-row.svg"
              alt="" />
            <div class="item-name">
              {{ benchmark.name || "Benchmark" }}
            </div>
            <span
              v-if="drawnNote(benchmark)"
              class="drawn-note">
              {{ drawnNote(benchmark) }}
            </span>
          </div>
          <template #content>
            <div class="context-menu-actions">
              <button
                class="button is-light"
                @click="duplicateBenchmark(i)">
                Duplicate
              </button>
              <button
                class="button is-danger is-light"
                @click="deleteBenchmark(i)">
                Delete
              </button>
            </div>
          </template>
        </b-tooltip>
      </div>
    </vuedraggable>

    <button
      class="mini-view-item button add-button"
      @click="addBenchmark">
      <img
        src="@/assets/icons/add-row.svg"
        alt="" />
      <div>Add benchmark</div>
    </button>
  </div>
</template>

<script>
  import vuedraggable from 'vuedraggable'
  import uniqid from 'uniqid'
  import { childErrors as _childErrors } from '@/components/reports/config/common/validation'
  import { newKpiBenchmark, newKpiMeasure } from '@/components/pdf_report/kpi_module/defaults'

  export default {
    name: 'MiniKpiModule',
    components: {
      vuedraggable,
    },
    props: {
      value: {
        type: Object,
        default: () => {},
      },
      selection: {
        type: Object,
        default: null,
      },
      measureErrors: {
        type: Array,
        default: () => [],
      },
      primaryRowErrors: {
        type: Array,
        default: () => [],
      },
      benchmarkErrors: {
        type: Array,
        default: () => [],
      },
    },
    computed: {
      module: {
        get () {
          return this.value || {}
        },
        set (value) {
          this.$emit('input', value)
        },
      },
    },
    mounted () {
      if (!this.module.key) {
        this.module.key = uniqid()
      }
      // A module saved before benchmarks existed arrives with the field
      // absent when the API has yet to be deployed; the outline adds to this
      // list, so it has to be a list.
      if (!this.module.benchmarks) {
        this.$set(this.module, 'benchmarks', [])
      }
      // Keys are for `v-for` and drag reordering only — they are not part of
      // the saved config, so they are stamped on whatever arrives without one.
      const keyed = [
        ...(this.module.kpi_measures || []),
        ...(this.module.benchmarks || []),
      ]
      keyed.forEach((item) => {
        if (!item.key) {
          this.$set(item, 'key', uniqid())
        }
      })
    },
    methods: {
      childErrors: _childErrors,
      isSelected (kind, index) {
        return this.selection?.kind === kind && this.selection?.index === index
      },
      select (kind, index) {
        this.$emit('open', { kind, index, })
      },
      addMeasure () {
        this.module.kpi_measures.push(newKpiMeasure())
        this.select('measure', this.module.kpi_measures.length - 1)
      },
      addBenchmark () {
        this.module.benchmarks.push(newKpiBenchmark())
        this.select('benchmark', this.module.benchmarks.length - 1)
      },

      // What a benchmark draws, for the benchmarks that do not draw both
      // lines. Silent for `both`, which is the common case and would
      // otherwise label every entry.
      drawnNote (benchmark) {
        if (benchmark.show === 'value') return 'value'
        if (benchmark.show === 'var') return 'change'
        return ''
      },

      isHero (index) {
        return !!this.module.kpi_measures[index]?.is_hero
      },

      duplicateMeasure (index) {
        this.duplicateAt(this.module.kpi_measures, index, 'measure')
      },
      deleteMeasure (index) {
        this.deleteAt(this.module.kpi_measures, index, 'measure')
      },
      duplicateBenchmark (index) {
        this.duplicateAt(this.module.benchmarks, index, 'benchmark')
      },
      deleteBenchmark (index) {
        this.deleteAt(this.module.benchmarks, index, 'benchmark')
      },

      // The primary row has no buttons on purpose: a card is its primary
      // value, so there is nothing to delete it down to.

      duplicateAt (list, index, kind) {
        // Deep copy: a shallow one leaves both entries sharing measure_config,
        // so editing either would change both. Fresh key: `key` is saved with
        // the config and is what v-for and drag reordering track.
        const copy = JSON.parse(JSON.stringify(list[index]))
        copy.key = uniqid()
        if (copy.name) {
          copy.name = `${copy.name} copy`
        }
        // Two headlines is not a state the layout has.
        delete copy.is_hero
        list.splice(index + 1, 0, copy)
        this.select(kind, index + 1)
      },

      deleteAt (list, index, kind) {
        list.splice(index, 1)
        if (this.selection?.kind !== kind) return

        // Selection is by index; re-point it so a delete above the selected
        // item does not swap which one is being edited.
        const current = this.selection.index
        if (current > index) {
          this.select(kind, current - 1)
        } else if (current === index) {
          if (list.length === 0) {
            this.$emit('open', null)
          } else {
            this.select(kind, Math.min(index, list.length - 1))
          }
        }
      },
    },
  }
</script>

<style lang="scss" scoped>
.data-tooltip {
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: left;

  h2 {
    font-size: 12px;
    font-weight: 600;
  }
}

.kpi-outline {
  margin-top: 8px;
  margin-left: 20px;

  .group-heading {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: $grayscale-3;
    margin: 14px 0 6px;
  }

  button {
    display: flex;
    align-items: flex-start;
    color: $primary;
    border: 1px solid rgba(0, 0, 0, 0.2);
    background-color: rgba(0, 0, 0, 0.04);
    justify-content: flex-start;

    &:hover {
      color: $primary;
      border: 1px solid rgba(0, 0, 0, 0.2);
    }
  }
}

.context-menu-actions {
  display: flex;
  flex-direction: column;

  button {
    margin-bottom: 4px;
    font-size: 12px;
  }
}

// The name takes the slack so a headline marker sits hard right, and a long
// measure name truncates instead of pushing the marker out of the card.
.item-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drawn-note {
  margin-left: auto;
  flex-shrink: 0;
  padding-left: 8px;
  font-size: 11px;
  color: $grayscale-3;
}

.hero-marker {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 16px;
  color: $primary;
}

.mini-view-item {
  cursor: pointer;
  transition: background-color 0.3s ease-in-out, color 0.3s;
  width: 100%;
  padding: 10px;
  display: flex;
  border-radius: 4px;
  align-items: center;
  font-size: 15px;
  background-color: white;
  border: 1px solid #f5f5f5;
  box-shadow: 0px 1px 4px 0px rgba(40, 39, 43, 0.02),
    0px 1px 2px 0px rgba(40, 39, 43, 0.06);

  &.add-button {
    background-color: rgba(0, 0, 0, 0.03);
    transition: background-color 0.1s;
    border: $border-1;
    display: flex;
    align-items: center;
    color: $grayscale-1 !important;

    &:hover {
      border: $border-1;
    }
  }

  img {
    width: 15px;
    height: 15px;
    margin-right: 10px;
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
    transition: background-color 0.1s;
  }

  &:hover:not(.error-border) {
    border: $border-1;
  }

  &.selected {
    background-color: $primary;
    color: white;

    img {
      filter: brightness(0) invert(1);
    }

    .drawn-note {
      color: rgba(255, 255, 255, 0.75);
    }
  }

  &.secondary {
    // The outline is already inset by 20px, so a full-width row indented by
    // another 20 overflowed the panel and cut its own right edge off. The
    // indent has to come out of the width, not be added to the offset.
    margin-left: 20px;
    width: calc(100% - 20px);
  }
}
</style>
